import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { getAuthorization } from "modules/auth/AuthorizationSelectors";
import { Privileges } from "modules/auth/Authorization";
import { ReportTagDTO } from "pages/reports/bi-dashboard-next/models/ReportTagDTO";
import { ReportTagInsertDTO } from "pages/reports/bi-dashboard-next/models/ReportTagInsertDTO";
import { DistinctTagDTO } from "pages/reports/bi-dashboard-next/models/DistinctTagDTO";
import getLocalApiUrl from "pages/reports/utils/getLocalApiUrl";

const createTag = async (
  reportId: number,
  tagData: ReportTagInsertDTO,
): Promise<ReportTagDTO> => {
  const response = await axios.post(
    getLocalApiUrl(`/reporting/reports/${reportId}/tags`),
    tagData,
  );
  return response.data;
};

const createMultipleTags = async (
  reportId: number,
  tagDataArray: ReportTagInsertDTO[],
): Promise<ReportTagDTO[]> => {
  const response = await axios.post(
    getLocalApiUrl(`/reporting/reports/${reportId}/tags/batch`),
    { tags: tagDataArray },
  );
  return response.data;
};

const deleteTag = async (reportId: number, tagId: number): Promise<void> => {
  await axios.delete(
    getLocalApiUrl(`/reporting/reports/${reportId}/tags/${tagId}`),
  );
};

const fetchDistinctTags = async (): Promise<DistinctTagDTO[]> => {
  const response = await axios.get(
    getLocalApiUrl(`/reporting/reports/distinct-tags`),
  );
  return response.data;
};

export const useReportTags = () => {
  const queryClient = useQueryClient();
  const authorization = useSelector(getAuthorization);
  const canManageTags = authorization.hasPrivileges([
    Privileges.MANAGE_SHARED_REPORTS,
  ]);

  const distinctTagsQuery = useQuery({
    queryKey: ["distinctTags"],
    queryFn: fetchDistinctTags,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 5 * 60 * 1000,
  });

  const createTagMutation = useMutation({
    mutationFn: ({
      reportId,
      tagData,
    }: {
      reportId: number;
      tagData: ReportTagInsertDTO;
    }) => createTag(reportId, tagData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reports"] });
      queryClient.invalidateQueries({ queryKey: ["distinctTags"] });
    },
  });

  const createMultipleTagsMutation = useMutation({
    mutationFn: ({
      reportId,
      tagDataArray,
    }: {
      reportId: number;
      tagDataArray: ReportTagInsertDTO[];
    }) => createMultipleTags(reportId, tagDataArray),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reports"] });
      queryClient.invalidateQueries({ queryKey: ["distinctTags"] });
    },
    onError: (error) => {
      console.error("Error creating tags:", error);
    },
  });

  const deleteTagMutation = useMutation({
    mutationFn: ({ reportId, tagId }: { reportId: number; tagId: number }) =>
      deleteTag(reportId, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reports"] });
      queryClient.invalidateQueries({ queryKey: ["distinctTags"] });
    },
  });

  return {
    distinctTags: distinctTagsQuery.data || [],
    isLoadingDistinctTags: distinctTagsQuery.isLoading,
    distinctTagsError: distinctTagsQuery.error,

    createTag: (params: { reportId: number; tagData: ReportTagInsertDTO }) => {
      if (!canManageTags) {
        console.warn("User does not have permission to create tags");
        return;
      }
      return createTagMutation.mutate(params);
    },
    isCreatingTag:
      createTagMutation.isPending || createMultipleTagsMutation.isPending,
    createTagError: createTagMutation.error,
    createMultipleTagsError: createMultipleTagsMutation.error,
    createMultipleTags: (params: {
      reportId: number;
      tagDataArray: ReportTagInsertDTO[];
    }) => {
      if (!canManageTags) {
        console.warn("User does not have permission to create tags");
        return;
      }
      return createMultipleTagsMutation.mutate(params);
    },
    deleteTag: (params: { reportId: number; tagId: number }) => {
      if (!canManageTags) {
        console.warn("User does not have permission to delete tags");
        return;
      }
      return deleteTagMutation.mutate(params);
    },
    isDeletingTag: deleteTagMutation.isPending,

    canManageTags,
  };
};
